//@mui
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";

// mui common components
import Table from "../components/common/MagicTable";
import Button from "../components/mui/Button";
import Box from "../components/mui/Box";
import Text from "../components/mui/Text";
import MagicDrawer from "../components/common/MagicDrawer_new";
import Header from "../components/common/MagicPageHeader";

import Modal from "../components/common/Modal";
import Stack from "../components/mui/Stack";

// User Api
import {
  useCreateRoleMutation,
  useListOfProjectsQuery,
  useUserRolesQuery,
  useDeleteRoleMutation,
  useLazyGetRolesByIdQuery,
  useUpdateRoleMutation,
  useListOfProgramsQuery,
} from "../store/apis/userApi";
import { useEffect, useState } from "react";
import RoleForm from "../components/userManagement/RoleForm";
import ImageComponent from "../components/mui/Image";
import { CircularProgress } from "@mui/material";
import { showToast } from "../helper/toaster";
import usePermission from "../helper/usePermission";

interface Props {
  selectedTab: any;
  chipLabelCount: number;
  rolesAccess: any;
}

const roleTableData = {
  roleName: "",
  projectsAccess: [],
  programAccess: [],
  moduleAccess: [],
};

const RoleTable: React.FC<Props> = ({
  selectedTab,
  chipLabelCount,
  rolesAccess,
}) => {
  const [formState, setFormState] = useState<any>({
    roleName: "",
    programAccess: [],
    projectsAccess: [],
    moduleAccess: [],
  });
  const [addFormState, setAddFormState] = useState<any>({
    roleName: "",
    programAccess: [],
    projectsAccess: [],
    moduleAccess: [],
  });

  const { permissions = {}, isAdmin } = usePermission();

  const [errors, setErrors] = useState<{ errorMessage?: string }>({});
  const [errorsAdd, setAddErrors] = useState({});
  const [roleId, setRoleId] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const [isOpenAddRoleSidebar, setIsOpenAddRoleSidebar] =
    useState<boolean>(false);

  const [isOpenEditRoleSidebar, setIsOpenEditRoleSidebar] =
    useState<boolean>(false);

  const [pagination, setPagination] = useState<{
    pageNo: number;
    pageSize: number;
  }>({
    pageNo: 1,
    pageSize: 10,
  });

  // State to track the row being deleted
  const [deletingRowId, setDeletingRowId] = useState<string | null>(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

  // api call start

  const {
    data: userRoles = {},
    isLoading: isLoadingRoles,
    isFetching: isFetchingUserRoles,
  } = useUserRolesQuery(pagination);

  const [createRole, { data: roleResponse = [], isLoading: isRoleCreating }] =
    useCreateRoleMutation();

  const [updateRole, { data: updatedRoles = [], isLoading: isUpdatingRoles }] =
    useUpdateRoleMutation();

  // Fetch project Data
  const { data: projectType = [], isLoading: isProjectLoading } =
    useListOfProjectsQuery();

  const { data: listOfPrograms, isLoading: isProgramLoading } =
    useListOfProgramsQuery();

  const [
    getRoleDataById,
    { isLoading: isGetRolesByIdLoading, data: getRolesByIdData },
  ] = useLazyGetRolesByIdQuery();

  const [deleteRole, { isLoading: isDeleting, isSuccess, error }] =
    useDeleteRoleMutation();

  const toggleDeleteModal = () =>
    setIsDeleteModalOpen((prevState) => !prevState);

  // api call end

  const totalPages = Math.ceil(userRoles?.rowsCount / 10);

  // Handles for drawwer open and close
  const handleAddRoleSideBar = () => {
    setAddFormState({
      roleName: "",
      projectsAccess: [],
      programAccess: [],
      moduleAccess:
        projectType?.map((project: any) => ({
          ...(project?.scopes?.map((item: any) => ({
            scopeIdentify: project?.value,
            scopeId: item?.value,
            scopeName: item?.label,
            view: false,
            read: false,
            write: false,
            delete: false,
            children:
              item?.children?.map((child: any) => ({
                childId: child?.value,
                scopeName: child?.label,
                view: false,
                read: false,
                write: false,
                delete: false,
              })) || [],
          })) || []),
        })) || [],
    });
    setIsOpenAddRoleSidebar(!isOpenAddRoleSidebar);
  };

  const handleEditRoleSideBar = async (rowData: any) => {
    setRoleId(rowData.value);
    let res: any = null;
    if (rowData.value) {
      res = await getRoleDataById(rowData.value);
    } else {
      setFormState({
        roleName: "",
        projectsAccess: [],
        programAccess: [],
        moduleAccess: [],
      });
    }

    if (projectType && res && res?.data && res?.data[0]?.scopeParents) {
      setFormState(roleTableData);
      // Merge projectType and res.data if both are available and have the expected structure
      const mergedModuleAccess = projectType?.reduce(
        (acc: any, project: any, index: any) => {
          const matchingRole = res.data[0].scopeParents.find(
            (roleProject: any) => roleProject.scopeParentId === project.value
          );

          setFormState((pre: any) => ({
            ...pre,
            roleName: res?.data[0]?.roleName,
            projectsAccess: res?.data[0]?.scopeParents?.map(
              (p: any) => p.scopeParentId
            ),
            programAccess:
              res.data[0]?.programType.map(
                (item: any) => item?.programTypeId
              ) || [],
          }));

          if (matchingRole) {
            // If matchingRole is found, merge its scope data with the project data
            const scopes = project?.scopes?.reduce(
              (scopeAcc: any, item: any, scopeIndex: any) => {
                const matchingScope = matchingRole?.scopes?.find(
                  (scope: any) => scope?.scopeId === item?.value
                );

                if (matchingScope) {
                  // Merge the scope data
                  scopeAcc[scopeIndex] = {
                    // ...item,
                    scopeIdentify: matchingRole?.scopeParentId,
                    scopeId: matchingScope?.scopeId,
                    scopeName: matchingScope?.scopeName,
                    view: matchingScope?.canRead || false,
                    read: matchingScope?.canWrite || false,
                    write: matchingScope?.canUpdate || false,
                    delete: matchingScope?.canDelete || false,
                    children: item?.children?.map((child: any) => {
                      const matchingChild = matchingScope?.scopeChild?.find(
                        (childScope: any) => childScope.childId === child.value
                      );
                      return {
                        // ...child,
                        childId: matchingChild?.childId,
                        scopeName: matchingChild?.childName,
                        view: matchingChild?.canRead || false,
                        read: matchingChild?.canWrite || false,
                        write: matchingChild?.canUpdate || false,
                        delete: matchingChild?.canDelete || false,
                      };
                    }),
                  };
                } else {
                  // If no matching scope is found, keep the project scope data with default false values
                  scopeAcc[scopeIndex] = {
                    // ...item,
                    scopeIdentify: matchingRole?.scopeParentId,
                    scopeId: item.value,
                    scopeName: item.label,
                    view: false,
                    read: false,
                    write: false,
                    delete: false,
                    children: item.children.map((child: any) => ({
                      childId: child.value,
                      scopeName: child.label,
                      view: false,
                      read: false,
                      write: false,
                      delete: false,
                    })),
                  };
                }
                return scopeAcc;
              },
              {}
            );

            // Add the merged project data along with the scopes
            acc[index] = scopes;
          } else {
            // If no matching role found, just add the project data without roles and permissions
            acc[index] = project?.scopes?.reduce(
              (scopeAcc: any, item: any, scopeIndex: any) => {
                scopeAcc[scopeIndex] = {
                  // ...item,
                  scopeIdentify: project.value,
                  scopeId: item.value,
                  scopeName: item.label,
                  view: false,
                  read: false,
                  write: false,
                  delete: false,
                  children:
                    item.children?.map((child: any) => ({
                      childId: child.value,
                      scopeName: child.label,
                      view: false,
                      read: false,
                      write: false,
                      delete: false,
                    })) || [],
                };
                return scopeAcc;
              },
              {}
            );
          }
          return acc;
        },
        {}
      );
      const finalOutput = Object.values(mergedModuleAccess).map(
        (item: any, index: any) => {
          return item;
        }
      );


      setFormState((pre: any) => ({
        ...pre,
        moduleAccess: finalOutput,
      }));
    }
    setIsOpenEditRoleSidebar(!isOpenEditRoleSidebar);
  };

  const handleDelete = (row: any) => {
    toggleDeleteModal();
    setDeletingRowId(row.value);
  };

  const onDelete = async () => {
    try {
      const res = await deleteRole({ roleId: deletingRowId });
      if (res?.data?.success) {
        showToast(res?.data?.message || "Role deleted successfully", "success");
        if (userRoles?.userRoles.length === 1) {
          setPagination((prevParams) => ({
            ...prevParams,
            pageSize: 10,
            pageNo: Math.max(prevParams.pageNo - 1, 1),
          }));
        }
      }
    } catch (error) {
      console.log(error);
    }
    toggleDeleteModal();
  };

  const onPageChange = async (event: any, page: number) => {
    setPagination((prevParams) => ({
      ...prevParams,
      pageSize: 10,
      pageNo: page,
    }));
  };

  const handleCheckboxChange = (
    rowIndex: number,
    nestedIndex: number,
    permissionType: string,
    value: boolean,
    isSubmodule: boolean = false,
    submoduleIndex?: number
  ) => {
    const updatedModuleAccess = formState?.moduleAccess?.map(
      (module: any, index: number) => {
        if (index === rowIndex) {
          if (isSubmodule && submoduleIndex !== undefined) {
            // Submodule is being toggled
            const updatedChildren = module[nestedIndex]?.children?.map(
              (child: any, childIdx: number) => {
                if (childIdx === submoduleIndex) {
                  return {
                    ...child,
                    [permissionType]: value,
                  };
                }
                return child;
              }
            );

            // Check if any submodule is checked
            const isAnyChildChecked = updatedChildren?.some(
              (child: any) => child[permissionType]
            );

            return {
              ...module,
              [nestedIndex]: {
                ...module[nestedIndex],
                [permissionType]: isAnyChildChecked,
                children: updatedChildren,
              },
            };
          } else {
            const updatedChildren = module[nestedIndex]?.children?.map(
              (child: any) => ({
                ...child,
                [permissionType]: value ? child[permissionType] : false,
              })
            );

            return {
              ...module,
              [nestedIndex]: {
                ...module[nestedIndex],
                [permissionType]: value,
                children: updatedChildren,
              },
            };
          }
        }
        return module;
      }
    );

    // Update the state
    setFormState((prevState: any) => ({
      ...prevState,
      moduleAccess: updatedModuleAccess,
    }));
  };

  const handleAddFormCheckbox = (
    rowIndex: number, // Index of the module in the `moduleAccess` array
    nestedIndex: number, // Index of the nested object (e.g., 0, 1)
    permissionType: string, // Permission type: "view", "read", "write", "delete"
    value: boolean, // New value for the checkbox
    isSubmodule: boolean = false, // Flag to indicate if it's a submodule
    submoduleIndex?: number // Index of the submodule, if applicable
  ) => {
    // Update the specific module's or submodule's permission
    const updatedModuleAccess = addFormState.moduleAccess.map(
      (module: any, index: number) => {
        if (index === rowIndex) {
          if (isSubmodule && submoduleIndex !== undefined) {
            // Handle submodule permission update
            const updatedChildren = module[nestedIndex].children.map(
              (child: any, childIdx: number) => {
                if (childIdx === submoduleIndex) {
                  return {
                    ...child,
                    [permissionType]: value, // Update the specific permission type for the submodule
                  };
                }
                return child;
              }
            );

            // Check if any child now has the permission enabled
            const isAnyChildChecked = updatedChildren.some(
              (child: any) => child[permissionType]
            );

            return {
              ...module,
              [nestedIndex]: {
                ...module[nestedIndex],
                [permissionType]: isAnyChildChecked || value,
                children: updatedChildren,
              },
            };
          } else {
            // Handle module permission update
            return {
              ...module,
              [nestedIndex]: {
                ...module[nestedIndex],
                [permissionType]: value, // Update the specific permission type for the module
              },
            };
          }
        }
        return module;
      }
    );


    // Set the updated state
    setAddFormState((prevState: any) => ({
      ...prevState,
      moduleAccess: updatedModuleAccess,
    }));
  };

  const handleAddMultiCheckbox = (
    value: string,
    selected: string[],
    key: "projectsAccess" | "programAccess"
  ) => {
    const updatedValue = selected.includes(value)
      ? selected.filter((v: any) => v !== value)
      : [...selected, value];
    setAddFormState({ ...addFormState, [key]: updatedValue });
  };

  const handleMultiCheckbox = (
    value: string,
    selected: string[],
    key: "projectsAccess" | "programAccess"
  ) => {
    const updatedValue = selected.includes(value)
      ? selected.filter((v: any) => v !== value)
      : [...selected, value];
    setFormState({ ...formState, [key]: updatedValue });
  };

  // Validate role form
  const validateForm = () => {
    if (
      !formState.roleName ||
      formState.roleName.length < 3 ||
      formState.roleName.length > 40
    ) {
      setErrors({
        errorMessage:
          "Role name must be 3-20 characters long and only contain alphabets.",
      });
      return false;
    }
    setErrors({ errorMessage: "" });
    return true;
  };
  const validateAddForm = () => {
    if (!addFormState.roleName.trim() || addFormState.roleName.length > 30) {
      setAddErrors({
        errorMessage:
          "Role name must be 3-20 characters long and only contain alphabets.",
      });
      return false;
    }
    setAddErrors({ errorMessage: "" });
    return true;
  };

  // onChnage Validation handler
  const handleAddOnChangeValidate = (value: string) => {
    setAddFormState({ ...addFormState, roleName: value });
    if (addFormState.roleName) {
      validateAddForm();
    }
  };
  const handleOnChangeValidate = (value: string) => {
    setFormState({ ...formState, roleName: value });
    if (formState.roleName) {
      validateForm();
    }
  };

  // handle for create role submit btn
  const handelOnRoleSubmit = async () => {
    // console.log({rowData})
    const rolePayload = {
      roleName: addFormState?.roleName,
      isAdmin: false,
      programTypes: addFormState?.programAccess?.map((item: any) => item),
      scopeParents: addFormState?.moduleAccess
        .map((scopes: any, scopeIndex: number) => {
          const scopeParentId = addFormState?.projectsAccess[scopeIndex]
            ?.toLowerCase()
            .replace(/\s+/g, "");

          const scope = addFormState?.moduleAccess
            .flatMap(
              (module: any) =>
                Object.keys(module).map((key) => ({
                  scopeIdentify: module[key].scopeIdentify,
                  scopeId: module[key].scopeId,
                  canUpdate: module[key].write,
                  canRead: module[key].view,
                  canWrite: module[key].read,
                  canDelete: module[key].delete,
                  scopeChild:
                    module[key]?.children?.map((child: any) => ({
                      childId: child.childId,
                      canUpdate: child.write,
                      canRead: child.view,
                      canWrite: child.read,
                      canDelete: child.delete,
                    })) || [],
                })) || {}
            )
            .filter((item: any) =>
              addFormState?.projectsAccess.some((access: string) =>
                access
                  .toLowerCase()
                  .split(" ")
                  .join("")
                  .includes(item?.scopeIdentify)
              )
            )
            .map(({ scopeIdentify, ...rest }: any): any => rest);

          return {
            scopeParentId,
            scope,
          };
        })
        .filter((item: any) => item.scopeParentId !== undefined),
    };

    if (validateAddForm()) {
      try {
        const res = await createRole(rolePayload);
        showToast(res?.data?.message || "Role created successfully");
      } catch (error) {
        console.error(error);
      } finally {
        handleAddRoleSideBar();
      }
    }
    // First, validate the form to ensure all required fields are filled
    if (!validateAddForm()) {
      console.log("Validation failed");
      return; // Stop the submission if the form is not valid
    }

    // reset the state
    setAddFormState({
      roleName: "",
      programAccess: [],
      projectsAccess: [],
      moduleAccess: [],
    });
  };

  const handleOnEditRoleSubmit = async () => {
    const rolePayload = {
      roleName: formState?.roleName,
      isAdmin: false,
      programTypes: formState?.programAccess?.map((item: any) => item),
      scopeParents: formState?.moduleAccess
        ?.map((scopes: any, scopeIndex: number) => {
          const scopeParentId = formState?.projectsAccess[scopeIndex]
            ?.toLowerCase()
            .replace(/\s+/g, "");

          const scope =
            formState?.moduleAccess
              ?.flatMap(
                (module: any) =>
                  Object.keys(module)?.map((key) => ({
                    scopeIdentify: module[key]?.scopeIdentify,
                    scopeId: module[key]?.scopeId,
                    canUpdate: module[key]?.write,
                    canRead: module[key]?.view,
                    canWrite: module[key]?.read,
                    canDelete: module[key]?.delete,
                    scopeChild:
                      module[key]?.children?.map((child: any) => ({
                        childId: child.childId,
                        canUpdate: child.write,
                        canRead: child.view,
                        canWrite: child.read,
                        canDelete: child.delete,
                      })) || [],
                  })) || {}
              )
              ?.filter((item: any) =>
                formState?.projectsAccess?.some((access: string) =>
                  access
                    .toLowerCase()
                    .split(" ")
                    .join("")
                    .includes(item?.scopeIdentify)
                )
              )
              .map(({ scopeIdentify, ...rest }: any): any => rest) || [];

          return {
            scopeParentId,
            scope,
          };
        })
        .filter((item: any) => item.scopeParentId !== undefined),
    };

    if (validateForm()) {
      try {
        const updatedRoleData = await updateRole({
          payload: rolePayload,
          roleId: roleId,
        });
        showToast(updatedRoleData?.data?.message);
      } catch (error) {
        console.log(error);
      }
      setIsOpenEditRoleSidebar(!isOpenEditRoleSidebar);
      // reset the state
      setFormState({
        roleName: "",
        programAccess: [],
        projectsAccess: [],
        moduleAccess: [],
      });
    }
    // First, validate the form to ensure all required fields are filled
    if (!validateForm()) {
      console.log("Validation failed");
      return; // Stop the submission if the form is not valid
    }
  };

  const addRolesBtn = [
    {
      text: "Add Role",
      variant: "contained",
      startIcon: <AddIcon />,
      onClick: handleAddRoleSideBar,
      sx: {
        borderRadius: "8px",
        border: "1px solid #a18218",
        backgroundColor: "#fff1b7",
        boxShadow: "none !important",
        "&:hover": {
          backgroundColor: "#e8d78e",
        },
      },
    },
  ];

  //Roles Columns
  const rolesColumns = [
    {
      field: "label",
      headerName: "Roles",
      flex: 1,
      minWidth: 220,
      sortable: false,
    },
    {
      field: "userAccessControl",
      headerName: "Programs",
      flex: 1,
      minWidth: 400,
      sortable: false,
      renderCell: (params: any) => {
        const roles = params.row?.userAccessControl || [];
        return (
          <Box
            sx={{
              display: "flex",
              gap: "5px",
              alignItems: "center",
              overflowX: "scroll",
              height: "100%",
              "&::-webkit-scrollbar": {
                width: "0px",
                height: "0px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#888",
                borderRadius: "10px",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                backgroundColor: "#555",
              },
            }}
          >
            {roles.length > 0 ? (
              roles.map((userRoles: any) => (
                <Text
                  key={userRoles}
                  sx={{
                    border: "1px solid #FEF7DA",
                    background: "#FEF7DA",
                    padding: "0px 10px",
                    borderRadius: "20px",
                  }}
                >
                  {userRoles}
                </Text>
              ))
            ) : (
              <Text>-</Text>
            )}
          </Box>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 0,
      minWidth: 170,
      sortable: false,
      renderCell: (props: { row: any }) => {
        return (
          <>
            <Button
              onClick={() => handleEditRoleSideBar(props.row)}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                color: "black",
                marginRight: "10px",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
              disabled={rolesAccess?.canUpdate ? false : true}
            >
              Edit
            </Button>
            <Button
              onClick={() => handleDelete(props.row)}
              variant="contained"
              sx={{
                textTransform: "capitalize",
                border: "1px solid #997906",
                borderRadius: "8px",
                backgroundColor: "#FFF1B7",
                padding: "8.2px 0px",
                color: "black",
                boxShadow: "none",
                "&:hover": {
                  boxShadow: "none",
                },
              }}
              disabled={rolesAccess?.canDelete ? false : true}
            >
              <ImageComponent
                src={"assets/images/login/Delete.svg"}
                sx={{ cursor: "pointer", height: "20px", width: "22px" }}
              />
            </Button>
          </>
        );
      },
    },
  ];

  return (
    <>
      {/* Delete modal */}
      <Modal open={isDeleteModalOpen} disableClose={true}>
        <>
          <Box
            padding={1}
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "12px",
              flexDirection: "column",
            }}
          >
            <Box height={"50px"}>
              <ImageComponent src="/assets/images/icon/warning.svg" />
            </Box>
            <Text
              sx={{ color: "#101828", fontWeight: "600", fontSize: "18px" }}
            >
              Are you sure you want to delete this?
            </Text>
            <Text
              sx={{
                color: "#667085",
                fontSize: "13px",
                fontWeight: "400",
                textAlign: "center",
              }}
            >
              By clicking on the Delete button you are making sure that you want
              to delete this permanently.
            </Text>

            <Stack flexDirection={"row"} width={"100%"} gap="15px">
              <Button
                variant="contained"
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #8D8D8D",
                  backgroundColor: "#fff",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#ededed",
                    boxShadow: "none",
                  },
                }}
                onClick={toggleDeleteModal}
              >
                No
              </Button>
              <Button
                sx={{
                  flex: 1,
                  borderRadius: "8px",
                  border: "1px solid #a18218",
                  backgroundColor: "#fef7da",
                  boxShadow: "none",
                  textTransform: "capitalize",
                  fontWeight: "600",
                  "&:hover": {
                    backgroundColor: "#e8d78e",
                    boxShadow: "none",
                  },
                }}
                loading={isDeleting}
                onClick={onDelete}
              >
                delete
              </Button>
            </Stack>
          </Box>
        </>
      </Modal>
      {/* Roles Table */}
      {selectedTab?.name === "Roles" && (
        <>
          <Header
            title="Roles"
            subTitle="Here you can view the list of all the Roles"
            chipLabel={`${userRoles?.rowsCount || 0} Roles`}
            buttons={rolesAccess?.canWrite ? addRolesBtn : []}
          />
          <Table
            // @ts-ignore
            rows={userRoles?.userRoles}
            count={totalPages}
            columns={rolesColumns}
            loading={isLoadingRoles}
            rowCellsSx={{ paddingLeft: "16px", textAlign: "start" }}
            headerSx={{
              textAlign: "start",
              fontWeight: 600,
              color: "#242424",
              padding: "13px 16px",
              backgroundColor: "#DADADA",
            }}
            tableSx={{ minHeight: "200px", width: "100%" }}
            handlePageChange={onPageChange}
            sx={{
              "& .MuiDataGrid-columnHeader": {
                backgroundColor: "#DADADA",
              },
              "& .MuiDataGrid-columnHeaderTitle": {
                fontWeight: 550,
                fontSize: "14px",
              },
              "& .MuiDataGrid-row": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "#FFFFFF",
              },
              "& .MuiDataGrid-cell": {
                display: "flex",
                alignItems: "center",
              },
            }}
            currentPage={pagination?.pageNo || 1}
            getRowId={(row: any) => row?.value}
          />

          {/* This sidebar opens on clicking Add Programs button */}
          <MagicDrawer
            title="Add Roles"
            subtitle="Here you can Add role"
            drawerOpen={isOpenAddRoleSidebar}
            onDrawerToggle={handleAddRoleSideBar}
            // width="500px"
            sx={{ width: "500px" }}
            actionComponent={() => (
              <>
                <Button
                  className="bun-outline"
                  onClick={handleAddRoleSideBar}
                  disabled={isRoleCreating}
                >
                  Cancel
                </Button>
                <Button
                  className="bun-filled"
                  onClick={handelOnRoleSubmit}
                  loading={isRoleCreating}
                >
                  Done
                </Button>
              </>
            )}
          >
            <RoleForm
              formState={addFormState}
              errors={errorsAdd}
              handleCheckboxChange={handleAddFormCheckbox}
              handleMultiCheckbox={handleAddMultiCheckbox}
              handleOnChangeValidate={handleAddOnChangeValidate}
              projectType={projectType}
              isLoading={false}
              listOfPrograms={listOfPrograms}
            />
          </MagicDrawer>

          {/*Role Edit drawer */}
          <MagicDrawer
            title="Edit Roles"
            subtitle="Here you can edit role"
            drawerOpen={isOpenEditRoleSidebar}
            onDrawerToggle={handleEditRoleSideBar}
            // width="500px"
            sx={{ width: "500px" }}
            actionComponent={() => (
              <>
                <Button
                  className="bun-outline"
                  onClick={() =>
                    setIsOpenEditRoleSidebar(!isOpenEditRoleSidebar)
                  }
                  disabled={isUpdatingRoles}
                >
                  Cancel
                </Button>
                <Button
                  className="bun-filled"
                  onClick={handleOnEditRoleSubmit}
                  loading={isUpdatingRoles}
                >
                  Done
                </Button>
              </>
            )}
          >
            <RoleForm
              formState={formState}
              errors={errors}
              handleCheckboxChange={handleCheckboxChange}
              handleMultiCheckbox={handleMultiCheckbox}
              handleOnChangeValidate={handleOnChangeValidate}
              projectType={projectType}
              isLoading={isLoading}
              listOfPrograms={listOfPrograms}
            />
          </MagicDrawer>
        </>
      )}
    </>
  );
};

export default RoleTable;
